// cart-gifts.js
// Cart mutations & promo logic (Free shipping only - no free gifts)

// ✅ Flag to prevent shipping protection auto-add when removing last items
window.__preventShippingProtectionAutoAdd = false;

function onCartReady(fn) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", fn);
  } else {
    fn();
  }
}

window.addEventListener("cart:updated", () => {
  if (window.scheduleAffirmRefresh) {
    window.scheduleAffirmRefresh("cart:updated");
  }
});

// If the page is restored from the back/forward cache mid-request (e.g. the
// shopper hit Buy it now / Shop Pay, then pressed Back), an add-to-cart
// button can be frozen in its disabled state with no fetch left to re-enable
// it. Reset every product-form button to its correct initial state on restore.
window.addEventListener("pageshow", (event) => {
  if (!event.persisted) return;
  document.querySelectorAll("product-form form").forEach((form) => {
    if (form.__submitButton) {
      form.__submitButton.disabled = !!form.__initialSubmitDisabled;
    }
  });
});

/* ---------- Basic cart change helpers + free gift tiers ---------- */

async function changeItemQuantity(id, quantity) {
  
  const data = { id, quantity };
  if (!window.cartDrawer) {
    console.error('[Cart] cartDrawer not available');
    return;
  }

  cartDrawer.classList.add("loading");
  
  // ✅ ONLY check for last item removal if quantity is 0 (complete removal)
  let needsShippingProtectionRemoval = false;
  
  if (quantity === 0) {
    try {
      const currentCart = await fetch("/cart.js", { credentials: "same-origin", cache: "no-store" }).then(r => r.json());
      const shipping = 55980472533316;
      const vipProductId = 55521574617412; // VIP Cuban Chain
      const jewelryCADProductId = 8631210705220;
      
      // Count real items (excluding VIP, shipping protection, CAD, and any
      // line a discount has brought to $0 — e.g. a Buy X Get Y "free" line,
      // which can even share the same variant_id as the paid line, just
      // under a different line key). final_price (not price) is what
      // reflects the post-discount amount, so it's the only reliable signal
      // for "is this line actually free right now."
      const realItems = currentCart.items.filter(item => {
        const vid = String(item.variant_id);
        if (vid === String(shipping)) return false;
        if (vid === String(vipProductId)) return false;
        if (String(item.product_id) === String(jewelryCADProductId)) return false;
        if (!item.price || item.price <= 0) return false;
        if (item.final_price <= 0) return false;
        return true;
      });

      // Find the item being removed
      const itemBeingRemoved = currentCart.items.find(i => {
        // id could be a key (string) or variant_id (number)
        return i.key === id || i.key === String(id) ||
               String(i.variant_id) === String(id) ||
               i.variant_id === id;
      });

      // Check if this item is a real item
      const isRealItem = itemBeingRemoved && realItems.some(ri => ri.key === itemBeingRemoved.key);

      // Only trigger special handling if:
      // 1. We're removing a real item (not a gift)
      // 2. There's only 1 real item left
      // 3. The item we're removing IS that last real item
      if (isRealItem && realItems.length === 1 && realItems[0].key === itemBeingRemoved.key) {
        window.__preventShippingProtectionAutoAdd = true;
        needsShippingProtectionRemoval = true;

        // Remove every other line that's currently free before removing the
        // last real item: shipping protection, the VIP gift, AND any Buy X
        // Get Y "free" line (matched by key, since it can share the same
        // variant_id as the item being removed). These free lines only exist
        // as a reward for the purchase we're about to delete, and Shopify
        // rejects (422) removing that purchase while its dependent free
        // line(s) are still in the cart. Without this, the customer's delete
        // click on their last product silently fails and the item appears
        // "stuck" until they remove the coupon themselves (which happens to
        // clear the free line(s) too).
        const companionItems = currentCart.items.filter((item) => {
          if (item.key === itemBeingRemoved.key) return false;
          const vid = String(item.variant_id);
          if (vid === String(shipping)) return true;
          if (vid === String(vipProductId)) return true;
          return item.final_price <= 0;
        });

        if (companionItems.length > 0) {
          for (const companionItem of companionItems) {
            try {
              await changeByKey(companionItem.key, 0);
            } catch (err) {
              console.warn('[Cart] Failed to remove companion item:', err);
            }
          }
          // Small delay to ensure companion items are removed
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
    } catch (err) {
      console.warn('[Cart] Could not check cart state:', err);
    }
  }
  
  // Now proceed with the actual item change
  try {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...data,
        sections: cartDrawer.getSectionsToRender().map((section) => section.id),
      }),
    });
    
    // Check if the response is OK
    if (!res.ok) {
      
      // If it's the last item and we get 422, refresh the drawer
      if (needsShippingProtectionRemoval && res.status === 422) {
        if (typeof refreshCartDrawer === 'function') {
          await refreshCartDrawer({ silent: false });
        }
        return;
      }
      
      // For other errors, try to recover
      throw new Error(`Cart change failed with status ${res.status}`);
    }
    
    const json = await res.json();
    
    // Ensure we have the sections before rendering
    if (json.sections && json.sections["cart-drawer"]) {
      checkFreeGift(json);
      cartDrawer.render(json.sections["cart-drawer"], "cart-drawer");
      window.dispatchEvent(new CustomEvent("cart:updated"));
    } else {
      console.warn('[Cart] Response missing sections, refreshing drawer');
      if (typeof refreshCartDrawer === 'function') {
        await refreshCartDrawer({ silent: false });
      }
    }
  } catch (err) {
    console.error("Error changing cart item quantity:", err);
    // Try to refresh the drawer to recover from the error
    if (typeof refreshCartDrawer === 'function') {
      try {
        await refreshCartDrawer({ silent: false });
      } catch (refreshErr) {
        console.error("Failed to refresh cart drawer:", refreshErr);
      }
    }
  } finally {
    cartDrawer.classList.remove("loading");
    
    // Only clear the flag if we set it
    if (needsShippingProtectionRemoval) {
      setTimeout(() => {
        console.log('[Cart] Clearing shipping protection prevention flag');
        window.__preventShippingProtectionAutoAdd = false;
      }, 1500);
    }
  }
}

// Visible feedback while an add-to-cart submission is in flight. Native
// `disabled` alone has no styling for the PDP's atc-button markup, so the
// button looked unresponsive even though the click was correctly blocked.
function setButtonLoading(button, isLoading) {
  button.classList.toggle("is-atc-loading", isLoading);

  const svgSpinner = button.querySelector(".svg-loader");
  if (svgSpinner) svgSpinner.style.display = isLoading ? "block" : "none";

  const label = button.querySelector("[data-add-to-cart-text]");
  if (label) label.style.visibility = isLoading ? "hidden" : "";

  const spinnerDiv = button.querySelector(".loading__spinner");
  if (spinnerDiv) spinnerDiv.classList.toggle("hidden", !isLoading);
}

if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        // A <product-form> can wrap more than one <form> (e.g. the main PDP
        // form plus the floating/sticky add-to-cart bar's own form) — every
        // one of them needs its own AJAX handler, not just the first.
        const forms = this.querySelectorAll("form");

        forms.forEach((form) => {
          form.__handledByAjax = true;

          const idInput = form.querySelector("[name=id]");
          if (idInput) idInput.disabled = false;

          const submitButton = form.querySelector('[type="submit"]');
          // Remember whether this button is *meant* to be disabled (sold out /
          // unavailable variant) so bfcache restores can be reset correctly.
          const initialSubmitDisabled = submitButton
            ? submitButton.disabled
            : false;

          form.addEventListener("submit", (evt) =>
            this.onSubmitHandler(evt, form, submitButton)
          );

          form.__submitButton = submitButton;
          form.__initialSubmitDisabled = initialSubmitDisabled;
        });

        this.forms = forms;
      }

      onSubmitHandler(evt, form, submitButton) {
        evt.preventDefault();
        if (submitButton) {
          submitButton.disabled = true;
          setButtonLoading(submitButton, true);
        }

        if (!window.cartDrawer) {
          form.submit();
          return;
        }

        const config = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/javascript",
            "X-Requested-With": "XMLHttpRequest",
          },
        };
        delete config.headers["Content-Type"];

        const formData = new FormData(form);
        formData.append(
          "sections",
          cartDrawer.getSectionsToRender().map((section) => section.id)
        );
        formData.append("sections_url", window.location.pathname);
        config.body = formData;

        fetch("/cart/add.js", config)
          .then((response) => response.json())
          .then((data) => {
            if (data.sections && data.sections["cart-drawer"]) {
              cartDrawer.render(data.sections["cart-drawer"], "cart-drawer");
            }

            window.dispatchEvent(new CustomEvent("cart:updated"));
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            cartDrawer.open();
            if (submitButton) {
              submitButton.disabled = false;
              setButtonLoading(submitButton, false);
            }
          });
      }
    }
  );
}

async function clearCart() {
  // ✅ Set flag when clearing cart to prevent shipping protection auto-add
  window.__preventShippingProtectionAutoAdd = true;
  
  if (!window.cartDrawer) return;
  cartDrawer.classList.add("loading");
  
  try {
    // Get current cart to check for shipping protection
    const currentCart = await fetch("/cart.js", { 
      credentials: "same-origin", 
      cache: "no-store" 
    }).then(r => r.json());
    
    const shipping = 55980472533316;
    const shippingProtectionItems = currentCart.items.filter(
      (item) => String(item.variant_id) === String(shipping)
    );
    
    // Remove shipping protection first if it exists
    if (shippingProtectionItems.length > 0) {
      for (const spItem of shippingProtectionItems) {
        try {
          await changeByKey(spItem.key, 0);
        } catch (err) {
          console.warn('[Cart] Failed to remove shipping protection:', err);
        }
      }
      // Small delay to ensure it's removed
      await new Promise(resolve => setTimeout(resolve, 200));
    }
  } catch (err) {
    console.warn('[Cart] Could not pre-remove shipping protection:', err);
  }
  
  fetch("/cart/clear?clear-cart-function", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    cache: "no-store",
  })
    .then((response) => {
      if (!response.ok) {
        console.error("Failed to clear the cart");
        return;
      }

      if (typeof refreshCartDrawer === "function") {
        return refreshCartDrawer({ silent: false });
      }
    })
    .catch((error) => {
      console.error("Error clearing the cart", error);
    })
    .finally(() => {
      if (typeof cartDrawer !== "undefined") {
        cartDrawer.classList.remove("loading");
      }
      // Clear flag after clearing completes
      setTimeout(() => {
        window.__preventShippingProtectionAutoAdd = false;
      }, 1500);
    });
}

function addToCart(data, options = {}) {
  const { silent = false } = options;

  if (!window.cartDrawer) return Promise.reject("Cart drawer not found");

  return new Promise((resolve, reject) => {
    jQuery.ajax({
      type: "POST",
      url: "/cart/add.js?sections=cart-drawer&addProduct_Ajax",
      data: JSON.stringify({ items: data }),
      contentType: "application/json",
      dataType: "json",
      success: function (response) {
        if (response.sections && response.sections["cart-drawer"]) {
          cartDrawer.render(response.sections["cart-drawer"], "cart-drawer");
        }

        if (!silent) {
          window.dispatchEvent(new CustomEvent("cart:updated"));
        }

        cartDrawer.classList.remove("loading");
        resolve(response);
      },
      error: function (xhr, status, error) {
        console.error("Error adding data to cart:", error);
        cartDrawer.classList.remove("loading");
        reject(error);
      },
    });
  });
}

function removeFromCart(items, options = {}) {
  const { silent = false } = options;

  if (!window.cartDrawer || !items.length)
    return Promise.reject("Invalid parameters");

  return new Promise((resolve, reject) => {
    let updateData = {};
    $.ajax({
      type: "GET",
      url: "/cart.js?removeProduct_Ajax",
      cache: false,
      dataType: "json",
      success: function (data) {
        let cartItems = data.items;
        items.forEach((item) => {
          for (let i = 0; i < cartItems.length; i++) {
            if (cartItems[i].variant_id == item) {
              updateData[cartItems[i].key] = 0;
              break;
            }
          }
        });
        $.ajax({
          type: "POST",
          url: "/cart/update.js?sections=cart-drawer&trim=removeProduct_Ajax",
          data: { updates: updateData },
          dataType: "json",
          success: function (cart) {
            if (cart.sections && cart.sections["cart-drawer"]) {
              cartDrawer.render(cart.sections["cart-drawer"], "cart-drawer");
            }

            if (!silent) {
              window.dispatchEvent(new CustomEvent("cart:updated"));
            }

            cartDrawer.classList.remove("loading");
            resolve(cart);
          },
          error: function (err) {
            console.error("Update Data Error:", err);
            cartDrawer.classList.remove("loading");
            reject(err);
          },
        });
      },
      error: function (err) {
        console.log("Error fetching cart data");
        cartDrawer.classList.remove("loading");
        reject(err);
      },
    });
  });
}

// Shared definition of "real" (paid, non-gift, non-VIP, non-shipping-protection)
// items so checkFreeGift() and ensureVipGift() can't disagree about whether the
// cart is actually empty of real product.
const VIP_PRODUCT_ID = 55521574617412; // VIP Cuban Chain
const JEWELRY_CAD_PRODUCT_ID = 8631210705220;
const SHIPPING_PROTECTION_VARIANT_ID = 55980472533316;

function hasRealItemsInCart(cart) {
  const items = (cart && cart.items) || [];
  return items.some((item) => {
    const vid = String(item.variant_id);

    if (vid === String(SHIPPING_PROTECTION_VARIANT_ID)) return false;
    if (vid === String(VIP_PRODUCT_ID)) return false;
    if (String(item.product_id) === String(JEWELRY_CAD_PRODUCT_ID)) return false;
    if (!item.price || item.price <= 0) return false;
    // final_price (post-discount) is what actually reflects whether this
    // line is free right now — a Buy X Get Y "free" line can still report a
    // nonzero item.price (the pre-discount unit price) even though it costs
    // the customer nothing, and can even share variant_id with a paid line.
    if (item.final_price <= 0) return false;

    return true;
  });
}

// A discount code stays attached to the cart/checkout session even after
// the cart is fully emptied (clearing items doesn't clear the applied
// discount), and the theme's own "discount"/"discount_code"/
// "checkout_discount_code" cookies persist independently of cart state too.
// So when a customer empties their cart, then comes back later and adds a
// new product, the old coupon silently reactivates on its own. Whenever we
// detect there's no real product left in the cart, proactively clear both
// the server-side discount session and these tracking cookies so the coupon
// actually goes away instead of quietly surviving in the background.
let __discountClearInFlight = false;
async function clearAppliedDiscountIfAny(cart_data) {
  const hasDiscount =
    Array.isArray(cart_data && cart_data.discount_codes) &&
    cart_data.discount_codes.length > 0;
  if (!hasDiscount || __discountClearInFlight) return;

  __discountClearInFlight = true;
  try {
    await fetch('/discount/clear?redirect=/', {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
  } catch (err) {
    console.warn('[Cart] Failed to clear discount on empty cart:', err);
  } finally {
    ['discount', 'discount_code', 'checkout_discount_code'].forEach((name) => {
      document.cookie = name + '=; Max-Age=0; path=/; SameSite=Lax';
      document.cookie =
        name + '=; Max-Age=0; path=/; domain=.' + location.hostname.replace(/^www\./, '') + '; SameSite=Lax';
    });
    __discountClearInFlight = false;
  }
}

function checkFreeGift(cart_data) {
  if (!cart_data || !Array.isArray(cart_data.items)) {
    console.warn('[FreeGifts] Invalid cart data provided');
    return;
  }
  if (cart_data.items.length === 0) {
    // console.log('[FreeGifts] Cart is empty');
    clearAppliedDiscountIfAny(cart_data);
    return;
  }

  // ✅ Only handling shipping protection and VIP now - NO free gifts
  const vipProductId = VIP_PRODUCT_ID;
  const jewelryCADProductId = JEWELRY_CAD_PRODUCT_ID;
  const shipping = SHIPPING_PROTECTION_VARIANT_ID;

  const itemsToFixQuantity = [];

  const vipProductItems = cart_data.items.filter(
    (item) => item.variant_id === vipProductId
  );
  const vipTotalQty = vipProductItems.reduce(
    (sum, item) => sum + (item.quantity || 0),
    0
  );

  // ✅ Check for real items (excluding VIP, CAD, shipping protection, 0-price items)
  const hasRealItems = hasRealItemsInCart(cart_data);

  if (!hasRealItems) {
    clearAppliedDiscountIfAny(cart_data);

    // Remove VIP items when no real items
    vipProductItems.forEach((item) => {
      itemsToFixQuantity.push({ key: item.key, quantity: 0 });
    });

    // ✅ Remove shipping protection when no real items
    const shippingProtectionItems = cart_data.items.filter(
      (item) => String(item.variant_id) === String(shipping)
    );
    shippingProtectionItems.forEach((item) => {
      itemsToFixQuantity.push({ key: item.key, quantity: 0 });
    });

    if (itemsToFixQuantity.length && typeof changeByKey === "function") {
      if (!window.cartDrawer) return;
      cartDrawer.classList.add("loading");

      Promise.all(
        itemsToFixQuantity.map((item) => changeByKey(item.key, item.quantity))
      )
        .then(() => {
          if (typeof refreshCartDrawer === 'function') {
            return refreshCartDrawer({ silent: true });
          }
        })
        .catch((err) => {
          console.error("Error removing items:", err);
          if (typeof refreshCartDrawer === 'function') {
            refreshCartDrawer({ silent: true });
          }
        })
        .finally(() => {
          if (window.cartDrawer) {
            cartDrawer.classList.remove("loading");
          }
        });
    }

    return;
  }

  // Enforce VIP quantity = 1 if present
  if (vipProductItems.length > 0) {
    if (vipTotalQty > 1 || vipProductItems.length > 1) {
      itemsToFixQuantity.push({ key: vipProductItems[0].key, quantity: 1 });
      for (let i = 1; i < vipProductItems.length; i++) {
        itemsToFixQuantity.push({ key: vipProductItems[i].key, quantity: 0 });
      }
    }
  }

  if (itemsToFixQuantity.length && typeof changeByKey === "function") {
    if (!window.cartDrawer) return;
    cartDrawer.classList.add("loading");

    Promise.all(
      itemsToFixQuantity.map((item) => changeByKey(item.key, item.quantity))
    )
      .then(() => {
        if (typeof refreshCartDrawer === 'function') {
          return refreshCartDrawer({ silent: true });
        }
      })
      .catch((err) => {
        console.error("Error fixing quantities:", err);
        if (typeof refreshCartDrawer === 'function') {
          refreshCartDrawer({ silent: true });
        }
      })
      .finally(() => {
        if (window.cartDrawer) {
          cartDrawer.classList.remove("loading");
        }
      });
  }
}

/* ---------- VIP Cuban Chain logic + fetch patch ---------- */

function getCart() {
  return fetch("/cart.js", {
    credentials: "same-origin",
    cache: "no-store",
  }).then((r) => r.json());
}

async function refreshCartDrawer(options = {}) {
  const { silent = false } = options;

  if (!window.cartDrawer || typeof cartDrawer.render !== "function") {
    console.warn("Cart drawer not ready yet, skipping refresh.");
    return;
  }
  try {
    const res = await fetch("/?sections=cart-drawer", {
      credentials: "same-origin",
      cache: "no-store",
    });
    const json = await res.json();
    cartDrawer.render(json["cart-drawer"], "cart-drawer");

    if (!silent) {
      window.dispatchEvent(new CustomEvent("cart:updated"));
    }
  } catch (e) {
    console.error("Cart drawer reload failed:", e);
  }
}

// --- VIP Cuban chain ---
const VIP_CODE = "vip";
const GIFT_VARIANT_ID = "55521574617412";
const GIFT_QUANTITY = 1;

function vipCookiePresent() {
  const c = document.cookie.toLowerCase();
  return /(;\s*|^)(discount|discount_code|checkout_discount_code)=vip(;|$)/.test(c);
}

function vipUrlPresent() {
  try {
    const p = location.pathname.toLowerCase();
    const q = new URLSearchParams(location.search.toLowerCase());
    return p.includes("/discount/vip") || q.get("discount") === "vip";
  } catch (_) {
    return false;
  }
}

function hasVipSignal(cart) {
  const cartHasVip =
    Array.isArray(cart?.discount_codes) &&
    cart.discount_codes.some((d) => (d.code || "").toLowerCase() === VIP_CODE);
  return cartHasVip || vipCookiePresent() || vipUrlPresent();
}

let __vipGiftBusy = false;

async function ensureVipGift({ maxRetries = 10, delay = 400 } = {}) {
  if (window.__vipGiftDisabledUntil && Date.now() < window.__vipGiftDisabledUntil) return;
  
  if (__vipGiftBusy) return;
  __vipGiftBusy = true;
  try {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const cart = await getCart();
      // The gift should only exist while there's real product in the cart —
      // otherwise this fights checkFreeGift(), which strips the gift once no
      // real items remain even though the discount code is still applied to
      // the cart (Shopify doesn't clear it just because the cart emptied).
      const vipActive = hasVipSignal(cart) && hasRealItemsInCart(cart);

      const giftItems = (cart.items || []).filter(
        (i) => String(i.variant_id) === GIFT_VARIANT_ID
      );
      const totalGiftQty = giftItems.reduce((n, i) => n + (i.quantity || 0), 0);

      if (vipActive) {
        if (!giftItems.length) {
          await addGift();
          await refreshCartDrawer({ silent: true });
        } else {
          const first = giftItems[0];
          if (totalGiftQty !== 1) {
            if ((first.quantity || 0) !== 1) {
              await changeByKey(first.key, 1);
            }
            for (let i = 1; i < giftItems.length; i++) {
              await changeByKey(giftItems[i].key, 0);
            }
            const afterClamp = await getCart();
            const recheck = (afterClamp.items || []).filter(
              (i) => String(i.variant_id) === GIFT_VARIANT_ID
            );
            const recheckQty = recheck.reduce((n, i) => n + (i.quantity || 0), 0);
            if (recheckQty !== 1) {
              await changeByVariant(GIFT_VARIANT_ID, 1);
            }
            await refreshCartDrawer({ silent: true });
          }
        }
      } else {
        for (const gi of giftItems) {
          await changeByKey(gi.key, 0);
        }
        if (giftItems.length) {
          await refreshCartDrawer({ silent: true });
        }
      }

      const verify = await getCart();
      const verifyGifts = (verify.items || []).filter(
        (i) => String(i.variant_id) === GIFT_VARIANT_ID
      );
      const verifyQty = verifyGifts.reduce((n, i) => n + (i.quantity || 0), 0);
      if ((vipActive && verifyQty === 1) || (!vipActive && verifyQty === 0)) break;

      await new Promise((r) => setTimeout(r, delay));
    }
  } catch (e) {
    console.warn("ensureVipGift error:", e);
  } finally {
    __vipGiftBusy = false;
  }
}

async function addGift() {
  await fetch("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    cache: "no-store",
    body: JSON.stringify({ id: GIFT_VARIANT_ID, quantity: GIFT_QUANTITY }),
  });
}

async function changeByKey(key, qty) {
  await fetch("/cart/change.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    cache: "no-store",
    body: JSON.stringify({ id: key, quantity: qty }),
  });
}

async function changeByVariant(variantId, qty) {
  await fetch("/cart/change.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    cache: "no-store",
    body: JSON.stringify({ id: variantId, quantity: qty }),
  });
}

// ✅ Shipping Protection quantity enforcement
window.enforceShippingProtectionQuantity = async function () {
  try {
    const cart = await getCart();
    const items = cart.items || [];

    const spRow = document.querySelector("[data-shipping-protection]");
    if (!spRow) return;

    const spVariantId = spRow.getAttribute("data-variant-id");
    if (!spVariantId) return;

    const spLines = items.filter((i) => String(i.variant_id) === spVariantId);

    if (spLines.length > 0) {
      const totalQty = spLines.reduce((sum, line) => sum + (line.quantity || 0), 0);

      if (totalQty > 1 || spLines.length > 1) {
        const first = spLines[0];
        await changeByKey(first.key, 1);

        for (let i = 1; i < spLines.length; i++) {
          await changeByKey(spLines[i].key, 0);
        }

        await refreshCartDrawer({ silent: true });
      }
    }
  } catch (e) {
    console.warn("enforceShippingProtectionQuantity error:", e);
  }
};

// Patch fetch only to enforce no-store on cart requests
(function patchFetchForCartOps() {
  const origFetch = window.fetch;
  window.fetch = async function (input, init = {}) {
    const url = typeof input === "string" ? input : (input && input.url) || "";

    if (/\/cart\//.test(url)) {
      init = init || {};
      if (!init.credentials) init.credentials = "same-origin";
      init.cache = "no-store";
    }

    return origFetch(input, init);
  };
})();

/* ---------- Boot & listeners ---------- */

onCartReady(function () {
  const atcButtons = document.querySelectorAll("[data-atc-button]");
  atcButtons.forEach((btn) => {
    if (btn.dataset.soldOut === "true") return;

    btn.disabled = false;
    btn.classList.remove("is-atc-loading");

    const label =
      btn.querySelector("[data-add-to-cart-text]") ||
      btn.querySelector(".atc-label");

    if (label) {
      try {
        const addText =
          (window.Shopify && Shopify.strings && Shopify.strings.addToCart) ||
          label.textContent ||
          "Add to cart";
        label.textContent = addText;
      } catch {}
    }
  });

  jQuery.getJSON("/cart.js", function (cart) {
    setTimeout(() => {
      checkFreeGift(cart); // Only manages VIP and shipping protection
    }, 300);

    if (cart.total_price === 0) {
      clearCart();
    }

    if (typeof cartDrawer !== "undefined") {
      cartDrawer.classList.remove("loading");
    }
  });

  setTimeout(() => {
    ensureVipGift({ maxRetries: 8, delay: 300 });
  }, 500);

  setTimeout(() => {
    if (window.enforceShippingProtectionQuantity) {
      window.enforceShippingProtectionQuantity();
    }
  }, 1200);
});

let __cartUpdateTimeout = null;

window.addEventListener("cart:updated", () => {
  ensureVipGift({ maxRetries: 6, delay: 300 });

  clearTimeout(__cartUpdateTimeout);
  __cartUpdateTimeout = setTimeout(() => {
    getCart().then(checkFreeGift); // Only manages VIP and shipping protection
  }, 600);

  if (window.enforceShippingProtectionQuantity) {
    window.enforceShippingProtectionQuantity();
  }
});

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    ensureVipGift({ maxRetries: 6, delay: 300 });
    getCart().then(checkFreeGift);

    if (window.enforceShippingProtectionQuantity) {
      window.enforceShippingProtectionQuantity();
    }
  }
});